import React from 'react';
import { useTranslation } from 'react-i18next';

import CartProductDiscount from 'components/Checkout/Cart/CartProduct/CartProductDiscount/CartProductDiscount';
import CartProductPrice from 'components/Checkout/Cart/CartProduct/CartProductPrice/CartProductPrice';
import CartProductStock from 'components/Checkout/Cart/CartProduct/CartProductStock/CartProductStock';
import CartProductWrapper from 'components/Checkout/Cart/CartProductWrapper/CartProductWrapper';
import useLargeSizeCookie from 'hooks/useLargeSizeCookie';
import useProductInfos from 'hooks/useProductInfos';
import { getBrandName } from 'services/product';
import { CartOfferType, CartProduct as CartProductType } from 'types/Cart';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';

import styles from './CheckoutSummaryProduct.module.scss';

export default function CheckoutSummaryProduct({
  product,
}: CheckoutSummaryProductType): JSX.Element | null {
  const { t } = useTranslation();
  const { isLSSku } = useLargeSizeCookie();
  const { reference, sku, size } = product;
  const { productInfos } = useProductInfos(sku, reference, size);
  const { totalPrice, quantity, stock } = product;
  const { totalCrossedOutPrice, discountPercentage = 0, title, infoOctipas } = {
    ...product,
  };

  if (!productInfos) return null;

  const {
    color,
    front_view,
    long_designation,
    name,
    size: sizeLabel,
    brand: brandLabel,
  } = productInfos;

  const brand =
    getBrandName((infoOctipas ?? {}) as SelectedProductAttributes) ||
    brandLabel;

  const formattedTitle = brand
    ? `${brand}® - ${title ?? long_designation ?? ''}`
    : title ?? long_designation ?? '';

  return (
    <CartProductWrapper
      classes={{
        container: styles.container,
        content: styles.content,
        details: styles.details,
        image: styles.image,
        title: styles.title,
      }}
      image={
        isLSSku(sku) ? (product as CartProductType).frontViewCurvy : front_view
      }
      title={formattedTitle}
    >
      <p className={styles.model}>{name}</p>
      <p className={styles.reference}>
        <b>
          {t('LABEL_TWO_POINTS', {
            label: t('LABEL_REF'),
          })}
        </b>
        {reference}
      </p>
      {stock && (
        <p className={styles.stock}>
          <CartProductStock stock={stock} />
        </p>
      )}
      <p>
        <b>{t('LABEL_TWO_POINTS', { label: t('LABEL_SIZE') })}</b>
        {sizeLabel}
      </p>
      <p>
        <b>{t('LABEL_TWO_POINTS', { label: t('LABEL_COLOUR') })}</b>
        {color}
      </p>
      <p>
        <b>{t('LABEL_TWO_POINTS', { label: t('LABEL_QUANTITY') })}</b>
        {quantity}
      </p>
      {+discountPercentage > 0 && (
        <CartProductDiscount
          className={styles.discount}
          discountPercentage={+discountPercentage}
        />
      )}
      <div className={styles.priceContainer}>
        <CartProductPrice
          classes={{
            crossedOutPrice: styles.crossedOutPrice,
            price: styles.price,
          }}
          crossedOutPrice={totalCrossedOutPrice}
          price={totalPrice}
        />
      </div>
    </CartProductWrapper>
  );
}

type CheckoutSummaryProductType = {
  product: CartProductType | CartOfferType;
};
